import { isEmpty } from 'lodash-es';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { ApplicationListItem } from '@/components/contacts/application-list/application-list-item';
import FriendApplicationListItem from '@/components/contacts/application-list/friend-application-list-item';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useFriendRequestCount from '@/hooks/contacts/use-friend-request-count';
import { useIsDesktop } from '@/hooks/use-user-platform';
import useFriendApplications from '@/hooks/users/use-friend-applications';
import type { PromiseLoaderReturnType } from '@/routes';
import ROUTES from '@/routes';

export default function FriendApplicationsRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { data } = useFriendApplications();
  const { markAllAsRead } = useFriendRequestCount();
  const [searchParams] = useTypedSearchParams(ROUTES.CONTACT.USERS.APPLICATIONS);
  const { dispatch } = useCommDispatcher();

  useEffect(() => {
    markAllAsRead();
  }, []);

  return (
    <PageLayout
      title={t('contact.applications.title')}
      headerConfig={{
        navigateBack: !isDesktop,
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
      }}>
      <div className='px-5 mt-4 desktop:overflow-auto desktop:pb-safe-offset-8 pb-safe-offset-4'>
        {!isEmpty(data?.applications) && (
          <>
            <h2 className='font-bold mb-4 text-body1'>{t('contact.applications.pending-applications')}</h2>

            <LoadingLayout promise={loaderData?.promises}>
              {() => (
                <ul className='flex w-full flex-col items-stretch mb-6 gap-6 desktop:overflow-y-auto'>
                  {data?.applications.map((application) => (
                    <li
                      key={application.id}
                      id={`pending-application-${application.id}`}>
                      <FriendApplicationListItem application={application} />
                    </li>
                  ))}
                </ul>
              )}
            </LoadingLayout>
          </>
        )}

        <h2 className='font-bold mb-4 text-body1'>{t('contact.applications.past-applications')}</h2>

        <ul className='flex w-full flex-col items-stretch gap-6 desktop:overflow-y-auto desktop:h-fit'>
          <LoadingLayout promise={loaderData?.promises}>
            {() =>
              data?.pastApplications.map((application) => (
                <li
                  key={application.id}
                  id={`past-application-${application.id}`}>
                  <ApplicationListItem application={application} />
                </li>
              ))
            }
          </LoadingLayout>
        </ul>
      </div>
    </PageLayout>
  );
}
